/**
 * API hooks for authenticated requests
 *
 * Updated for Vite: Uses import.meta.env instead of process.env
 */

import { type ApiClient, createApiClient } from '@cortex/shared/api-client';
import { useCallback } from 'react';

import { createClient } from '@/lib/supabase/client';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * Hook that returns a getter for a typed Hono RPC API client.
 *
 * Creates a fresh client per call so the JWT is always current.
 *
 * @example
 * ```tsx
 * const getApi = useApi();
 * const api = await getApi();
 * const res = await api.users.$get();
 * ```
 */
export function useApi() {
  return useCallback(async (): Promise<ApiClient> => {
    const supabase = createClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    return createApiClient(API_BASE_URL, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  }, []);
}

/**
 * Hook that returns an auth-aware fetch function for edge cases
 * where the Hono RPC client can't be used (FormData uploads, SSE streams).
 *
 * @example
 * ```tsx
 * const fetchWithAuth = useFetchWithAuth();
 * const res = await fetchWithAuth('/requests/123/develop/stream');
 * ```
 */
export function useFetchWithAuth() {
  return useCallback(
    // eslint-disable-next-line no-undef
    async (path: string, options?: RequestInit): Promise<Response> => {
      const supabase = createClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      const headers = new Headers(options?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return fetch(`${API_BASE_URL}${path}`, { ...options, headers });
    },
    []
  );
}
